import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';

const FLAGS = { IN:'🇮🇳', DE:'🇩🇪', NL:'🇳🇱', FR:'🇫🇷', GB:'🇬🇧', US:'🇺🇸' };
const MEDALS = ['🥇','🥈','🥉'];
const PODIUM_BORDER = ['#F59E0B55','#94A3B855','#CD7C2F55'];

export default function LeaderboardPage() {
  const [scope, setScope] = useState('global');
  const [data, setData] = useState({ leaderboard: [], my_rank: null });
  const [loading, setLoading] = useState(true);
  const loggedIn = !!localStorage.getItem('evapp_token');

  useEffect(() => {
    setLoading(true);
    api.get('/leaderboard', { params: { scope, limit: 50 } })
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [scope]);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--navy)', paddingTop: 56, fontFamily: 'Inter, sans-serif' }}>
      {/* Hero */}
      <div style={{ background: 'linear-gradient(180deg,var(--slate) 0%,var(--navy) 100%)', borderBottom: '1px solid var(--border)', padding: '36px 20px 28px', textAlign: 'center' }}>
        <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 8 }}>🏆 Leaderboard</h1>
        <p style={{ color: 'var(--text-3)', fontSize: 14, maxWidth: 380, margin: '0 auto' }}>
          Top EV drivers keeping the charging network honest. Every report counts.
        </p>
      </div>

      <div style={{ maxWidth: 620, margin: '0 auto', padding: '24px 16px 60px' }}>
        {/* My rank banner */}
        {data.my_rank && (
          <div style={{ background: 'var(--teal-dim)', border: '1px solid var(--teal-mid)', borderRadius: 10, padding: '12px 16px', marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: 'var(--teal)', fontWeight: 600 }}>Your rank</span>
            <span style={{ fontSize: 20, fontWeight: 800, color: 'var(--teal)', fontFamily: 'JetBrains Mono, monospace' }}>#{data.my_rank}</span>
          </div>
        )}

        {/* Scope toggle */}
        <div style={{ display: 'flex', background: 'var(--slate)', borderRadius: 12, padding: 3, marginBottom: 20, border: '1px solid var(--border)' }}>
          {[['global','🌍 Global'],['country','🏠 My Country']].map(([s, label]) => (
            <button key={s} onClick={() => setScope(s)} style={{
              flex: 1, padding: '9px 0', borderRadius: 10, border: 'none',
              background: scope === s ? 'var(--navy)' : 'transparent',
              color: scope === s ? 'var(--text-1)' : 'var(--text-3)',
              fontSize: 13, fontWeight: 600, cursor: 'pointer',
              boxShadow: scope === s ? '0 2px 8px rgba(0,0,0,0.3)' : 'none',
              transition: 'all 0.15s',
            }}>{label}</button>
          ))}
        </div>

        {loading ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {[1,2,3,4,5].map(i => (
              <div key={i} style={{ height: 64, background: 'var(--slate)', borderRadius: 12, animation: 'shimmer 1.5s infinite', backgroundSize: '200% 100%', backgroundImage: 'linear-gradient(90deg,var(--slate) 25%,var(--slate-2) 50%,var(--slate) 75%)' }} />
            ))}
          </div>
        ) : data.leaderboard.length === 0 ? (
          /* Empty state — but useful, not just a sad message */
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <div style={{ fontSize: 56, marginBottom: 16 }}>🏆</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>No reports yet</h3>
            <p style={{ fontSize: 14, color: 'var(--text-3)', marginBottom: 24, lineHeight: 1.6 }}>
              Be the first on the leaderboard. Report a charger status on the map to earn points and claim the top spot.
            </p>
            <Link to="/" style={{ display: 'inline-block', padding: '11px 24px', borderRadius: 10, background: 'var(--teal)', color: '#F0F4FF', fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
              Open Map & Start Reporting
            </Link>
            {!loggedIn && (
              <p style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 16 }}>
                <Link to="/login" style={{ color: 'var(--teal)', textDecoration: 'none' }}>Sign in</Link> to earn points and appear here.
              </p>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {data.leaderboard.map((u, i) => (
              <div key={u.id} style={{
                display: 'flex', alignItems: 'center', gap: 14,
                padding: '14px 16px', borderRadius: 12,
                background: i < 3 ? 'var(--slate)' : 'var(--navy-2)',
                border: `1px solid ${i < 3 ? PODIUM_BORDER[i] : 'var(--border)'}`,
                transition: 'transform 0.1s',
              }}>
                {/* Rank */}
                <div style={{ width: 32, textAlign: 'center', flexShrink: 0 }}>
                  {i < 3
                    ? <span style={{ fontSize: 22 }}>{MEDALS[i]}</span>
                    : <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-3)', fontFamily: 'JetBrains Mono, monospace' }}>#{u.rank}</span>
                  }
                </div>

                {/* Avatar */}
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: i === 0 ? 'linear-gradient(135deg,#F59E0B,#EF4444)' : i === 1 ? 'linear-gradient(135deg,#94A3B8,#64748B)' : i === 2 ? 'linear-gradient(135deg,#CD7C2F,#92400E)' : 'var(--slate-2)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 15, fontWeight: 700, color: 'white',
                }}>
                  {(u.name || '?')[0].toUpperCase()}
                </div>

                {/* Info */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-1)' }} >{u.name || 'Anonymous'}</span>
                    {u.country && <span style={{ fontSize: 14 }}>{FLAGS[u.country] || ''}</span>}
                    {u.badges?.includes('community_hero') && (
                      <span style={{ fontSize: 9, background: 'var(--teal-dim)', color: 'var(--teal)', padding: '2px 6px', borderRadius: 6, fontWeight: 700, letterSpacing: '0.3px', border: '1px solid var(--teal-mid)' }}>HERO</span>
                    )}
                    {u.premium && (
                      <span style={{ fontSize: 9, background: '#F59E0B22', color: '#F59E0B', padding: '2px 6px', borderRadius: 6, fontWeight: 700, border: '1px solid #F59E0B44' }}>PRO</span>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{u.report_count} reports</div>
                </div>

                {/* Points */}
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: i < 3 ? 'var(--teal)' : 'var(--text-2)', fontFamily: 'JetBrains Mono, monospace', lineHeight: 1 }}>{u.points}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>pts</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* CTA for non-logged-in */}
        {!loggedIn && data.leaderboard.length > 0 && (
          <div style={{ marginTop: 24, background: 'var(--slate)', border: '1px solid var(--border)', borderRadius: 14, padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 6 }}>Want to appear here?</div>
            <div style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 14 }}>Sign up free, report chargers, earn points.</div>
            <Link to="/login" style={{ display: 'inline-block', padding: '10px 24px', borderRadius: 9, background: 'var(--teal)', color: '#F0F4FF', fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
              Join ChargeSure
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
